import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, ShieldCheck, AlertTriangle, Clock } from "lucide-react";

const INITIAL = {
  full_name: "",
  dob: "",
  phone: "",
  address_line1: "",
  address_line2: "",
  city: "",
  state: "",
  pincode: "",
  pan: "",
  aadhaar_last4: "",
  bank_holder_name: "",
  bank_account_number: "",
  bank_ifsc: "",
  business_type: "individual",
};

export default function CounselorKYC() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const nextUrl = params.get("next") || "/counselor";

  const [existing, setExisting] = useState(null);
  const [form, setForm] = useState(INITIAL);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/counselor-kyc/mine")
      .then((r) => {
        setExisting(r.data);
        if (r.data?.submitted) {
          // Prefill only non-sensitive fields for a re-submission after rejection
          setForm((f) => ({
            ...f,
            full_name: r.data.full_name || "",
            dob: r.data.dob || "",
            phone: r.data.phone || "",
            address_line1: r.data.address_line1 || "",
            address_line2: r.data.address_line2 || "",
            city: r.data.city || "",
            state: r.data.state || "",
            pincode: r.data.pincode || "",
            aadhaar_last4: r.data.aadhaar_last4 || "",
            bank_holder_name: r.data.bank_holder_name || "",
            bank_ifsc: r.data.bank_ifsc || "",
            business_type: r.data.business_type || "individual",
          }));
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (busy) return;
    // Basic client-side validation for the tricky fields
    const pan = form.pan.trim().toUpperCase();
    const ifsc = form.bank_ifsc.trim().toUpperCase();
    if (!/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(pan)) return toast.error("PAN must be like ABCDE1234F.");
    if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(ifsc)) return toast.error("IFSC must be 11 characters like HDFC0001234.");
    if (!/^\d{4}$/.test(form.aadhaar_last4)) return toast.error("Aadhaar last 4 digits must be 4 numbers.");
    if (!/^\d+$/.test(form.bank_account_number)) return toast.error("Bank account number must be digits only.");
    setBusy(true);
    try {
      await api.post("/counselor-kyc/submit", { ...form, pan, bank_ifsc: ifsc });
      toast.success("KYC submitted — Counseltaria will review it shortly.");
      if (typeof refresh === "function") await refresh();
      const r = await api.get("/counselor-kyc/mine");
      setExisting(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="p-16 text-center" data-testid="kyc-loading">Loading…</div>;

  const status = existing?.status || user?.kyc_status || "not_submitted";
  const isVerified = status === "verified";
  const isPending = status === "pending_review";
  const isRejected = status === "rejected";
  const revenueShare = user?.counselor_revenue_share ?? 100;

  return (
    <div className="max-w-3xl mx-auto px-6 py-14" data-testid="kyc-page">
      <span className="label-eyebrow">Payout setup</span>
      <h1 className="font-serif-display text-4xl sm:text-5xl mt-2 mb-3">KYC & bank verification</h1>
      <p className="mb-8 text-sm" style={{ color: "var(--text-secondary)" }}>
        We route your {revenueShare}% share of each booking directly to your verified bank account via
        Razorpay. Fill this once — Counseltaria's team reviews it, then payouts turn on automatically.
      </p>

      {/* Status card */}
      {existing?.submitted && (
        <div
          data-testid="kyc-status-card"
          className="card-soft p-5 mb-8 flex items-start gap-4"
          style={{
            border: "1px solid var(--border-soft)",
            background: isVerified
              ? "rgba(91,117,83,0.06)"
              : isRejected
                ? "rgba(230,178,58,0.08)"
                : "rgba(217,177,87,0.08)",
          }}
        >
          {isVerified ? (
            <CheckCircle2 className="w-8 h-8 flex-shrink-0" style={{ color: "var(--brand)" }} />
          ) : isRejected ? (
            <AlertTriangle className="w-8 h-8 flex-shrink-0" style={{ color: "var(--accent)" }} />
          ) : (
            <Clock className="w-8 h-8 flex-shrink-0" style={{ color: "#B48A3A" }} />
          )}
          <div>
            <div className="font-serif-display text-lg">
              {isVerified && "KYC verified — payouts are active."}
              {isPending && "KYC pending admin review."}
              {isRejected && "KYC needs another look."}
            </div>
            <div className="text-xs mt-1" style={{ color: "var(--text-secondary)" }}>
              {isVerified && existing.razorpay_linked_account_id && (
                <>Razorpay linked account: <code>{existing.razorpay_linked_account_id}</code></>
              )}
              {isVerified && !existing.razorpay_linked_account_id && (
                <>Verified locally. Razorpay Route linked account will be created once Route is enabled on the platform.</>
              )}
              {isPending && "We usually review within 1 business day. You can still accept bookings while we verify."}
              {isRejected && (existing.rejection_reason || "Please fix the highlighted issues and re-submit.")}
            </div>
          </div>
        </div>
      )}

      {/* Form fields */}
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Full legal name" value={form.full_name} onChange={(v) => set("full_name", v)} testid="kyc-full-name" required />
        <Field label="Date of birth" type="date" value={form.dob} onChange={(v) => set("dob", v)} testid="kyc-dob" required />
        <Field label="Phone" value={form.phone} onChange={(v) => set("phone", v)} testid="kyc-phone" placeholder="+91 98xxxxxxxx" required />
        <Field label="Business type" value={form.business_type} onChange={(v) => set("business_type", v)} testid="kyc-business-type" as="select" options={[["individual", "Individual"], ["proprietorship", "Sole Proprietorship"]]} />

        <Field label="Address line 1" value={form.address_line1} onChange={(v) => set("address_line1", v)} testid="kyc-address1" required className="sm:col-span-2" />
        <Field label="Address line 2 (optional)" value={form.address_line2} onChange={(v) => set("address_line2", v)} testid="kyc-address2" className="sm:col-span-2" />
        <Field label="City" value={form.city} onChange={(v) => set("city", v)} testid="kyc-city" required />
        <Field label="State" value={form.state} onChange={(v) => set("state", v)} testid="kyc-state" required />
        <Field label="Pincode" value={form.pincode} onChange={(v) => set("pincode", v)} testid="kyc-pincode" required />
        <Field label="PAN" value={form.pan} onChange={(v) => set("pan", v.toUpperCase())} testid="kyc-pan" placeholder="ABCDE1234F" required />
        <Field label="Aadhaar (last 4 digits only)" value={form.aadhaar_last4} onChange={(v) => set("aadhaar_last4", v.replace(/\D/g, "").slice(0, 4))} testid="kyc-aadhaar" required />

        <div className="sm:col-span-2 pt-4">
          <div className="label-eyebrow">Bank account for payouts</div>
        </div>
        <Field label="Account holder name" value={form.bank_holder_name} onChange={(v) => set("bank_holder_name", v)} testid="kyc-bank-holder" required />
        <Field label="Account number" value={form.bank_account_number} onChange={(v) => set("bank_account_number", v.replace(/\D/g, ""))} testid="kyc-bank-account" required />
        <Field label="IFSC" value={form.bank_ifsc} onChange={(v) => set("bank_ifsc", v.toUpperCase())} testid="kyc-bank-ifsc" placeholder="HDFC0001234" required />
      </div>

      <div className="mt-8 flex gap-3 items-center">
        <button data-testid="kyc-submit" disabled={busy} onClick={submit} className="btn-primary">
          {busy ? "Submitting…" : existing?.submitted ? "Re-submit for review" : "Submit for review"}
        </button>
        <button data-testid="kyc-later" onClick={() => nav(nextUrl)} className="btn-secondary">
          I'll finish this later
        </button>
      </div>

      <div className="mt-8 text-xs" style={{ color: "var(--text-secondary)" }}>
        <ShieldCheck className="inline w-3 h-3 mr-1" style={{ color: "var(--brand)" }} />
        Your KYC data is encrypted at rest and used only for payout verification via Razorpay.
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testid, type = "text", required, placeholder, className = "", as, options }) {
  return (
    <label className={`block ${className}`}>
      <span className="text-xs uppercase tracking-widest" style={{ color: "var(--text-secondary)" }}>
        {label}{required ? " *" : ""}
      </span>
      {as === "select" ? (
        <select
          data-testid={testid}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="input mt-1 w-full"
        >
          {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      ) : (
        <input
          data-testid={testid}
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          required={required}
          className="input mt-1 w-full"
        />
      )}
    </label>
  );
}
